/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-lines */
import React, { useCallback, useContext, useMemo } from 'react';
import { ControlledDateTimePickerDatatestid } from '../../../constants/index.js';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerT } from '../../../react-desc-prop-types.js';
import { defaultMetaInfo, weekDays } from '../../../utils/constants.js';
import { compareTwoDatesDayEquality, getDateStringFromDay, getDateValuesFromDate } from '../../../utils/dateHelpers.js';
import { getAriaDayFromDateString } from '../../../utils/stringHelpers.js';
import { CalendarContext } from './CalendarContext.js';
import { StyledDayBtn } from './Styleds.js';
import { useGetDayFlags } from './useGetDayFlags.js';

interface CalendarDayPropsT {
  metaDay: DSControlledDateTimePickerT.MetaMonthDay;
  colIndex?: number;
  isInvisible?: boolean;
}
const displayNone = { display: 'none' };

export const Day = ({ metaDay, colIndex, isInvisible }: CalendarDayPropsT): JSX.Element => {
  const { day, isCurrMonthDay, isHidden, dayBtnRef, dayAsString } = metaDay;
  const {
    handleChangeComposedDateString,
    getProps,
    props: { preventCloseOnSelection, readOnly, applyAriaDisabled, lastElementRef },
    appOnMonthChange,
    appOnDayChange,
    appOnYearChange,
    isWithTimeWheelToo,
    trackFocusCalendarMetafocusedDay,
    isControllerOnly,
  } = useContext(ControlledDateTimePickerContext);

  const { focusedDay, daysArray, handleDayOnKeyDown, handleFocusMetaDayByDay, closeCalendar } =
    useContext(CalendarContext);
  const isFocusedMetaDay = focusedDay?.day ? compareTwoDatesDayEquality(metaDay?.day, focusedDay?.day) : false;
  /*
   * PUI-18077 https://jira.elliemae.io/browse/PUI-18077
   *
   * Fallback logic that mirrors the behavior in:
   * ./useKeyboardHandlers.tsx (tryToFocusDayRegion)
   *
   * When opening the calendar without an explicit focusedDay, this ensures that
   * in controller-only mode, Shift+Tab from the first focusable element focuses
   * the first current-month day instead of the previous month button.
   *
   * Once focusedDay is established, this fallback is no longer used.
   */
  const firstCurrMonthDay = useMemo(() => daysArray.find((metaMonthDay) => metaMonthDay.isCurrMonthDay), [daysArray]);
  const isFirstCurrMonthFallback = useMemo(() => {
    if (!isControllerOnly) return false;
    if (focusedDay?.day) return false;
    if (!firstCurrMonthDay?.day) return false;
    return compareTwoDatesDayEquality(day, firstCurrMonthDay.day);
  }, [isControllerOnly, focusedDay?.day, firstCurrMonthDay?.day, day]);

  // the following flags are useful to be able to condition aria-* attributes via slots
  //   for example, the date-range-picker can chose to apply different aria-labels to the buttons based on these flags.
  const dayFlags = useGetDayFlags({ metaDay, isFirstCurrMonthFallback });
  const dayAriaLabel = useMemo(
    () => `${getAriaDayFromDateString(dayAsString)}, ${weekDays[metaDay.day.getDay()]}`,
    [dayAsString, metaDay],
  );
  const {
    isSelected,
    isFocused,
    isDisabled,
    isOutOfRange,
    isStartRangeDay,
    isEndRangeDay,
    isDayInRangeImproper,
    isDayInRange,
  } = dayFlags;
  const ownerPropsArguments = React.useRef({ ...dayFlags, metaDay });
  ownerPropsArguments.current = { ...dayFlags, metaDay };
  const getOwnerPropsArguments = React.useCallback(() => ownerPropsArguments.current, []);

  const handleOnDayClick = useCallback(
    (e: DSControlledDateTimePickerT.OnInternalValuesChangeEvent) => {
      handleFocusMetaDayByDay(day);
      if (!isDisabled && !isOutOfRange && !readOnly && !applyAriaDisabled) {
        const newDateString = getDateStringFromDay(day);
        const metaInfo = { ...defaultMetaInfo, newDateString };
        const { month, day: dayNumber, year } = getDateValuesFromDate(day);
        appOnMonthChange(`${month + 1}`, e, metaInfo);
        if (dayNumber) appOnDayChange(`${dayNumber}`, e, metaInfo);
        appOnYearChange(`${year}`, e, metaInfo);
        handleChangeComposedDateString(newDateString, metaInfo);
        trackFocusCalendarMetafocusedDay();
        if (!isWithTimeWheelToo && !preventCloseOnSelection) closeCalendar();
      }
    },
    [
      handleFocusMetaDayByDay,
      day,
      isDisabled,
      isOutOfRange,
      readOnly,
      applyAriaDisabled,
      appOnMonthChange,
      appOnDayChange,
      appOnYearChange,
      handleChangeComposedDateString,
      trackFocusCalendarMetafocusedDay,
      isWithTimeWheelToo,
      preventCloseOnSelection,
      closeCalendar,
    ],
  );

  const handleInnerRef = useCallback(
    (ButtonDomNode: HTMLButtonElement) => {
      window.requestAnimationFrame(() => {
        dayBtnRef.current = ButtonDomNode;
        if (isFocused) ButtonDomNode?.focus?.();
        /**
         * PUI-17922 https://jira.elliemae.io/browse/PUI-17922
         *
         * This allows consumers to plug this calendar into an external
         * focus trap by exposing the last focusable element through
         * `lastElementRef`.
         *
         * In the currently known scenarios (DSDataTable filters) that
         * last element is the day that is focused in the calendar when
         * the component is used in `controller-only` mode.
         *
         * Since this is a targeted bug fix, it is only applied for this
         * case for now (YAGNI). In the future we should review, for each
         * component configuration, which element should really be exposed
         * as `lastElementRef`.
         */
        if (lastElementRef && isControllerOnly && (isFocusedMetaDay || isFirstCurrMonthFallback)) {
          lastElementRef.current = ButtonDomNode;
        }
      });
    },
    [dayBtnRef, lastElementRef, isFocused, isFocusedMetaDay, isControllerOnly, isFirstCurrMonthFallback],
  );

  const handleOnDayFocus = useCallback(() => {
    trackFocusCalendarMetafocusedDay();
  }, [trackFocusCalendarMetafocusedDay]);

  const dayClassModifiers = useMemo(
    () =>
      `dayWrapper ${isSelected ? 'selectedDay ' : ''}${isDisabled ? 'disabledDay ' : ''}${
        isOutOfRange ? 'outOfRangeDay ' : ''
      }${isFocused ? 'focusedDay ' : ''}${isStartRangeDay ? 'startRangeDay ' : ''}${
        isDayInRangeImproper ? 'inRangeImproperDay ' : ''
      }${isDayInRange ? 'inRangeDay ' : ''}${isEndRangeDay ? 'endRangeDay ' : ''}${
        isCurrMonthDay ? '' : 'notCurrentMonth'
      }
      ${readOnly || applyAriaDisabled ? 'readOnly' : ''}`,
    [
      isSelected,
      isDisabled,
      isOutOfRange,
      isFocused,
      isStartRangeDay,
      isDayInRangeImproper,
      isDayInRange,
      isEndRangeDay,
      isCurrMonthDay,
      readOnly,
      applyAriaDisabled,
    ],
  );

  let dataTestid: (typeof ControlledDateTimePickerDatatestid)['CALENDAR'][keyof (typeof ControlledDateTimePickerDatatestid)['CALENDAR']] =
    ControlledDateTimePickerDatatestid.CALENDAR.DAY;

  if (isSelected) dataTestid = ControlledDateTimePickerDatatestid.CALENDAR.SELECTED_DAY;
  if (isFocused) dataTestid = ControlledDateTimePickerDatatestid.CALENDAR.FOCUSED_DAY;
  if (isFocused && isSelected) dataTestid = ControlledDateTimePickerDatatestid.CALENDAR.SELECTED_FOCUSED_DAY;
  if (isStartRangeDay) dataTestid = ControlledDateTimePickerDatatestid.CALENDAR.START_RANGE_DAY;
  if (isEndRangeDay) dataTestid = ControlledDateTimePickerDatatestid.CALENDAR.END_RANGE_DAY;
  if (isDayInRange) dataTestid = ControlledDateTimePickerDatatestid.CALENDAR.IN_RANGE_DAY;

  return (
    <div
      className={dayClassModifiers}
      style={isHidden ? displayNone : undefined}
      role="gridcell"
      {...(Number.isInteger(colIndex) && { 'aria-colindex': colIndex })}
    >
      <StyledDayBtn
        $readOnlyStyles={readOnly || applyAriaDisabled}
        aria-pressed={isSelected || isStartRangeDay || isDayInRange || isEndRangeDay ? 'true' : 'false'}
        aria-label={dayAriaLabel}
        aria-disabled={isOutOfRange || isDisabled || readOnly || applyAriaDisabled}
        /**
         * applyAriaDisabled is intentionally NOT passed here.
         *
         * DSButtonV2 with applyAriaDisabled suppresses the click event before onClick
         * fires. This prevents handleFocusMetaDayByDay from running, so focusedDay never
         * updates and the focus ring never appears — while keyboard navigation silently
         * anchors to the clicked position, causing offset arrow behavior.
         *
         * readOnly and applyAriaDisabled must produce the same click UX on day buttons:
         * focus moves (ring shows), value does not change. The value-change guard in
         * handleOnDayClick already covers both (!readOnly && !applyAriaDisabled).
         * aria-disabled above is already set explicitly, so AT state is unaffected.
         */
        aria-hidden={!!isInvisible}
        buttonType="raw"
        size="m"
        onClick={handleOnDayClick} // DSButton triggers this with "spacebar" and "enter" too...
        onFocus={handleOnDayFocus}
        onKeyDown={(e: React.KeyboardEvent<Element>) => {
          handleDayOnKeyDown(e, metaDay);
        }}
        innerRef={handleInnerRef}
        /**
         * PUI-17432 https://jira.elliemae.io/browse/PUI-17432
         *
         * We tried to avoid this in PUI-17922 by adding `lastElementRef`.
         * That works when the last element is focused programmatically by useFocusTrap logic,
         * but when going through the calendar and the last element is within another
         * component, on Shift+Tab the focus goes to the next month instead of the
         * last focused or selected day.
         *
         * This ensures that when using controller-only mode, focus moves back to the
         * last focused day.
         */
        tabIndex={isControllerOnly && (isFocusedMetaDay || isFirstCurrMonthFallback) ? 0 : -1}
        data-isfocused={isFocused}
        data-testid={dataTestid}
        type="button"
        getOwnerProps={getProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {`${day.getDate()}`}
      </StyledDayBtn>
    </div>
  );
};
